import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { IResponse } from "@app/lib/models/response.model";
import { BaseHttpService } from "@app/lib/services/base-http.service";
import { AppConfiguration } from '@lib/services/app-config.service';
import { Observable, Subject, tap } from "rxjs";
import { ETaskScreenName, Holiday, IAssignedTask, IAssignedTaskEvent, IMyAssignment, IMyAssignmentParams, TaskModel } from "../models/assigned-task.model";
import { TAny } from "@app/lib/models/common.type";
import { ICaseItem, ICaseSearchRequestBody, ISearchRequestParams } from "../models/case-assignment.model";

@Injectable({
    providedIn: 'root'
})
export class AssignedTaskService extends BaseHttpService {

    private _assignTask: Subject<IAssignedTaskEvent> = new Subject();
    private _taskScreenName = ETaskScreenName;

    public assignTask$ = this._assignTask.asObservable();

    constructor(
        http: HttpClient,
        appConfig: AppConfiguration,
        private router: Router,
    ) {
        super(http, appConfig);
    }

    public getTasks() {
        return this.get<IResponse<IAssignedTask[]>>('tasks/assigned.htm');
    }
    public getConstants() {
        return this.get<IResponse<TAny>>('home/constants.htm');
    }
    public getHoliday() {
        return this.get<IResponse<Holiday[]>>('calender/fetch/holidays-date.htm');
    }

    public assignTaskClick(task: IAssignedTask) {
        const event: IAssignedTaskEvent = this.getTaskByType(task);
        if (!this.router.routerState.snapshot.url.includes(event.url)) {
            this.router.navigate(['/dashboard', event.url])
                .then(() => {
                    this._assignTask.next(event);
                });
        } else {
            this._assignTask.next(event);
        }
    }

    public getMyAssignmentsHearing(queryParams: IMyAssignmentParams) {
        return this.get<IResponse<IMyAssignment[]>>('my-assignments/fetch.htm', { params: { ...queryParams } });
    }

    public getMyAssignmentsJudgement(queryParams: IMyAssignmentParams) {
        return this.get<IResponse<IMyAssignment[]>>('judgementDate/my-assignments/fixed-dates.htm', { params: { ...queryParams } });
    }

    private getTaskByType(task: IAssignedTask): IAssignedTaskEvent {
        const event: IAssignedTaskEvent = {
            name: '',
            task: task,
            url: ''
        };

        // 1. Define the mapping inside the function so 'this' works perfectly
        const taskRouteMapping: Record<string, { url: string; name: any }> = {
            // === HEARING SCHEDULE ===
            "FIX HEARING DATE": { url: 'hearing-schedule', name: this._taskScreenName.HEARINGSCHEDULE },
            "FIX MOTION HEARING DATE": { url: 'hearing-schedule', name: this._taskScreenName.HEARINGSCHEDULE },

            // === HEARING NOTICE ===
            "PREPARE HEARING SUMMONS": { url: 'hearing-notice', name: this._taskScreenName.HEARINGNOTICE },
            "PREPARE HEARING NOTICE": { url: 'hearing-notice', name: this._taskScreenName.HEARINGNOTICE },
            "PREPARE MOTION HEARING NOTICE": { url: 'hearing-notice', name: this._taskScreenName.HEARINGNOTICE },

            // === HEARING PROCESS ===
            "CONDUCT HEARING PROCESS": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "CONDUCT MOTION HEARING PROCESS": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "PREPARE JUDGEMENT": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "RULING": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "RECORD DRAFT DRAWNUP ORDER": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "RECORD SIGNED DRAWNUP ORDER": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "RECORD DRAFT ENROLLED ORDER": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },
            "RECORD SIGNED ENROLLED ORDER": { url: 'hearing-process', name: this._taskScreenName.HEARINGPROCESS },

            // === FEE ASSESSMENT ===
            "FEE ASSESSMENT": { url: 'fee-assessment', name: this._taskScreenName.FEEASSESSMENT },

            // === RECORD FEE PAYMENT ===
            "RECORD FEE PAYMENT": { url: 'record-fee-payment', name: this._taskScreenName.RECORDFEEPAYMENT },

            // === COURT / PANEL / ASSIGNMENT ===
            "ALLOCATE COURTROOM": { url: 'court-panel-assignment', name: this._taskScreenName.COURTPANEL },
            "JUDGE ASSIGNMENT": { url: 'court-panel-assignment', name: this._taskScreenName.COURTPANEL },

            // === DOCUMENTS RECEPTION ===
            "CASE ASSIGNMENT": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "OATH AFFIDAVIT": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "RECORD APPELLANT'S BRIEF": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "SERVICE OF MOTION": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "SERVICE OF AFFIDAVIT": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "VALIDATE DOCUMENTS": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "EFILING REQUEST": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "FTM REQUEST": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD AFFIDAVIT OF SERVICE OF HEARING SUMMONS": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD AFFIDAVIT OF SERVICE OF HEARING NOTICE": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD PROOF OF SERVICE OF HEARING NOTICE": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD AFFIDAVIT OF SERVICE OF OATH SUMMONS": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD AFFIDAVIT OF SERVICE OF OATH NOTICE": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD PROOF OF SERVICE OF OATH NOTICE": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "UPLOAD PROOF OF SERVICE OF MOTION HEARING NOTICE": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "RECORD HEARING NOTICE DOCUMENT": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },
            "RECORD HEARING NOTICE": { url: 'documents-reception', name: this._taskScreenName.DOCUMENTRECEPTION },

            // === JUDGEMENT SCHEDULE ===
            "FIX RULING DATE": { url: 'judgement-schedule', name: this._taskScreenName.JUDGEMENTSCHEDULE },
            "FIX JUDGEMENT DATE": { url: 'judgement-schedule', name: this._taskScreenName.JUDGEMENTSCHEDULE },
        };

        const taskName = (task.typeName || '').trim().toUpperCase();
        const routeConfig = taskRouteMapping[taskName];

        // 1. Assign URL route layout path normally
        event.url = routeConfig?.url || 'documents-reception';

        // 2. STRICTOR CHECK: Accept 0 or valid values, fallback ONLY if undefined/null
        event.name = (routeConfig && routeConfig.name !== undefined && routeConfig.name !== null)
            ? routeConfig.name
            : this._taskScreenName.DOCUMENTRECEPTION;

        return event;
    }

    IsTaskCompleted(docModel: TAny): Observable<TAny> {
        return this.post<TaskModel, TAny>('tasks/done.htm', docModel).pipe(tap(response => {
            //console.log('Response:', response);  // Log the response here
        })
        );
    }

    searchCase(reqBody: ICaseSearchRequestBody): Observable<IResponse<ICaseItem[]>> {
        return this.post<IResponse<ICaseItem[]>, ICaseSearchRequestBody>('case/search/by/case-number.htm', reqBody);
    }
}